/**
 * CompanyBrandingHub — iter-218r (Feb 2026)
 *
 * New intermediate hub introduced per user request:
 *   لوحة التحكم ▸ التحكم والمعلومات للمنصات ▸ ASB606 ADMIN (للتحكم)
 *   ▸ التحكم بإدارة الشركة ▸ ختم ومسمى الشركة
 *
 * Groups the two company-identity settings under one roof so admins can find
 * them without hunting through category tabs or the Employees hub:
 *   1. مسمى الشركة  → dedicated page (was buried inside CertificateSubjectsControl → "company" tab)
 *   2. ختم الشركة   → CompanyStampPage (moved out of Employees Settings)
 *
 * Route: /control-panel-settings/company-mgmt/branding
 */
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Building2, Stamp, ChevronLeft, ChevronRight } from 'lucide-react';
import DashboardLayout from '../../components/DashboardLayout';
import { useLanguage } from '../../context/LanguageContext';
import { usePerms } from '../../permissions/PermissionsContext';

export default function CompanyBrandingHub() {
  const navigate = useNavigate();
  const { visiblePages } = usePerms();
  const { language } = useLanguage();
  const isAr = language === 'ar';
  const Arrow = isAr ? ChevronLeft : ChevronRight;

  const CARDS = [
    {
      id: 'name',
      testid: 'branding-name-card',
      titleAr: 'مسمى الشركة',
      titleEn: 'Company Name',
      descAr:  'الاسم بالعربي والإنجليزي — يظهر تلقائياً في كل شهادة "لمن يهمه الأمر" و"تهنئة"',
      descEn:  'Arabic + English name — auto-injected into every "To-Whom-It-May-Concern" and Congratulations certificate',
      icon:  Building2,
      color: '#3B9EDC',
      route: '/control-panel-settings/company-mgmt/branding/name',
    },
    {
      id: 'stamp',
      testid: 'branding-stamp-card',
      titleAr: 'ختم الشركة',
      titleEn: 'Company Stamp',
      descAr:  'الختم الرسمي — يُختم به على الشهادات المعتمدة وأي مستند رسمي',
      descEn:  'Official stamp — placed on approved certificates and formal documents',
      icon:  Stamp,
      color: '#F59E0B',
      route: '/control-panel-settings/company-mgmt/employees/stamp',
    },
  ];

  return (
    <DashboardLayout
      title="ختم ومسمى الشركة"
      titleEn="Company Stamp & Name"
      subtitle="إدارة الاسم الرسمي والختم — يُستخدمان في كل شهادة معتمدة"
      subtitleEn="Manage the official name and stamp — reused on every approved certificate"
      screenCode="FZLC-BRANDING"
      backPath="/control-panel-settings/company-mgmt"
      onBack={() => navigate('/control-panel-settings/company-mgmt')}
      breadcrumb={[
        { labelAr: 'لوحة التحكم', labelEn: 'Dashboard', path: '/dashboard' },
        { labelAr: 'التحكم والمعلومات للمنصات', labelEn: 'Platform Control & Info', path: '/dashboard?view=platform-control' },
        { labelAr: 'ASB606 ADMIN (للتحكم)', labelEn: 'ASB606 ADMIN (Control)', path: '/control-panel-settings' },
        { labelAr: 'التحكم بإدارة الشركة', labelEn: 'Company Management Control', path: '/control-panel-settings/company-mgmt' },
        { labelAr: 'ختم ومسمى الشركة', labelEn: 'Company Stamp & Name' },
      ]}
    >
      <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="company-branding-hub">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
          {visiblePages(CARDS, 'route').map((c, i) => (
            <motion.button
              key={c.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.06 }}
              whileHover={{ y: -5 }}
              onClick={() => navigate(c.route)}
              className="group bg-white/[0.02] backdrop-blur-xl rounded-2xl p-6 border transition-all text-start"
              style={{ borderColor: `${c.color}30` }}
              data-testid={c.testid}
            >
              <div className="flex items-start gap-4">
                <div className="w-14 h-14 rounded-xl flex items-center justify-center border transition-transform group-hover:scale-110 shrink-0"
                     style={{ backgroundColor: `${c.color}15`, borderColor: `${c.color}40` }}>
                  <c.icon className="h-7 w-7" style={{ color: c.color }} />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-lg font-bold text-white mb-1 flex items-center gap-2">
                    {isAr ? c.titleAr : c.titleEn}
                    <Arrow className="w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity" style={{ color: c.color }} />
                  </h3>
                  <p className="text-sm text-white/60 leading-relaxed">
                    {isAr ? c.descAr : c.descEn}
                  </p>
                </div>
              </div>
            </motion.button>
          ))}
        </div>
      </section>
    </DashboardLayout>
  );
}
